import { DateTime } from 'luxon'

import BlogArticleAuthorQ from '@/queries/BlogArticleAuthorQ'
import BlogArticleQ from '@/queries/BlogArticleQ'
import CertificationRequestQ from '@/queries/CertificationRequestQ'
import CourseQ from '@/queries/CourseQ'
import { TopicType } from '@/queries/constants'
import LearnerFeedbackQ from '@/queries/LearnerFeedbackQ'
import PostFeedbackQ from '@/queries/PostFeedbackQ'
import SeriesHostQ from '@/queries/SeriesHostQ'
import SeriesQ from '@/queries/SeriesQ'
import SeriesTemplateAssessmentQ from '@/queries/SeriesTemplateAssessmentQ'
import SeriesTemplateQ from '@/queries/SeriesTemplateQ'
import SessionFeedbackQ from '@/queries/SessionFeedbackQ'
import SessionHostQ from '@/queries/SessionHostQ'
import SessionQ from '@/queries/SessionQ'
import SessionRegistrationQ from '@/queries/SessionRegistrationQ'
import SessionTemplateQ from '@/queries/SessionTemplateQ'
import TopicQ from '@/queries/TopicQ'
import TrainingQ from '@/queries/TrainingQ'
import TutorAuditFeaturedQ from '@/queries/TutorAuditFeaturedQ'
import TutorAuditQ from '@/queries/TutorAuditQ'
import UserQ from '@/queries/UserQ'
import UserVolunteerRoleQ from '@/queries/UserVolunteerRoleQ'
import VolunteerRoleQ from '@/queries/VolunteerRoleQ'
import VolunteerTeamQ from '@/queries/VolunteerTeamQ'
import WorldMemberQ from '@/queries/WorldMemberQ'
import WorldQ from '@/queries/WorldQ'
import { type Query, SQL } from '@/client/query'
import type { BlogArticleDetails } from '@/client/services/blog'
import { LEARNER_FEEDBACK_TYPE_TUTOR_TO_LEARNER } from '@/client/services/learnerFeedback/constants'
import { EMPTY_RICH_TEXT_VALUE } from '@/client/services/richText/constants'
import { getSeriesTimeInfo } from '@/client/services/series'
import {
  type SeriesTemplateDetails,
  SeriesTemplatePeerReviewVisibility,
  type SessionTemplateDetails,
} from '@/client/services/template'
import { SERIES_TEMPLATE_TYPE_STANDARD } from '@/client/services/template/constants'

import type { FeaturedFeedback } from '@/components/Feedback/types'

import type {
  CertificationRequestDetails,
  ChannelDetails,
  SeriesBase,
  SeriesDetails,
  SessionDetails,
  TopicDetails,
  UserDetails,
  UserVolunteerRoleDetails,
  UserWithMemberDetails,
  WorldDetails,
  WorldMemberDetails,
} from '../types'
import {
  CHANNEL_QUERY_TO_FIELDS,
  USER_QUERY_TO_FIELDS,
  WORLD_QUERY_TO_FIELDS,
} from './internal/constants'
import getFieldsFromQuery from './internal/getFieldsFromQuery'
import { worldBaseFromQuery } from './internal/helpers'

export function worldMemberDetailsFromQuery(worldMember: Query): WorldMemberDetails | null {
  const id = worldMember(WorldMemberQ.id)
  const level = worldMember(WorldMemberQ.level) ?? 'member'
  const joinedAt = DateTime.fromISO(`${worldMember(WorldMemberQ.created_at)}`)
  const goal = worldMember(WorldMemberQ.goal)
  const snoozeGoalUntil = worldMember(WorldMemberQ.snooze_goal_until)
  const snoozeGoalUntilDateTime =
    snoozeGoalUntil == null ? null : DateTime.fromISO(`${snoozeGoalUntil}`)
  const details = worldMember(WorldMemberQ.details)
  const helpIntents = details?.helpIntents ?? []

  return id == null
    ? null
    : { level, joinedAt, goal, snoozeGoalUntil: snoozeGoalUntilDateTime, helpIntents }
}

export function userDetailsFromQuery<K extends (keyof UserDetails)[]>(
  fields: [...K],
  { user }: { user: Query }
): Pick<UserDetails, K[number]> {
  return getFieldsFromQuery({
    fields,
    params: { query: user },
    getter: USER_QUERY_TO_FIELDS,
  })
}

export function userWithMemberDetailsFromQuery({
  worldMember,
}: {
  worldMember: Query
}): UserWithMemberDetails {
  const userDetails = userDetailsFromQuery(
    [
      'avatarUrl',
      'bio',
      'displayName',
      'id',
      'points',
      'pronouns',
      'roles',
      'username',
      'activeAnniversary',
      'timezone',
      'keyClubDistrictName',
    ],
    { user: worldMember }
  )
  const memberDetails = worldMemberDetailsFromQuery(worldMember)
  const isVerified = worldMember(UserQ.isVerified()) ?? false

  return { ...userDetails, memberDetails, isVerified }
}

export function worldDetailsFromQuery<K extends (keyof WorldDetails)[]>(
  fields: [...K],
  { world, userId }: { world: Query; userId: number | null }
): Pick<WorldDetails, K[number]> {
  return getFieldsFromQuery({
    fields,
    params: { query: world, userId },
    getter: WORLD_QUERY_TO_FIELDS,
  })
}

export function certificationRequestDetailsFromQuery({
  certificationRequest,
}: {
  certificationRequest: Query
}): CertificationRequestDetails {
  const createdAt = certificationRequest(CertificationRequestQ.created_at)
  const approvedAt = certificationRequest(CertificationRequestQ.approved_at)
  const readyForReviewAt = certificationRequest(CertificationRequestQ.ready_for_review_at)
  const rejectedAt = certificationRequest(CertificationRequestQ.rejected_at)

  return {
    id: certificationRequest(CertificationRequestQ.id) ?? 0,
    createdAt: DateTime.fromISO(`${createdAt}`),
    approvedAt: approvedAt == null ? null : DateTime.fromISO(`${approvedAt}`),
    readyForReviewAt: readyForReviewAt == null ? null : DateTime.fromISO(`${readyForReviewAt}`),
    reviewReason: certificationRequest(CertificationRequestQ.review_reason),
    rejectedAt: rejectedAt == null ? null : DateTime.fromISO(`${rejectedAt}`),
  }
}

export function topicDetailsFromQuery({ topic }: { topic: Query }): TopicDetails {
  return {
    id: topic(TopicQ.id) ?? 0,
    iconUrl: topic(TopicQ.icon) ?? '',
    name: topic(TopicQ.name) ?? '',
    courseName: topic(CourseQ.name) ?? '',
    type: topic(TopicQ.type) ?? TopicType.other,
    weight: topic(TopicQ.weight) ?? 0,
    kaUrl: topic(TopicQ.ka_url),
  }
}

export function seriesBaseFromQuery({ series }: { series: Query }): SeriesBase {
  const hosts = series.many(
    SeriesHostQ.where({
      seriesId: SeriesQ.id,
      approved: true,
      include: [{ association: 'User', required: true }],
      order: [[SeriesHostQ.role, 'desc']],
    })
  )

  return {
    id: series(SeriesQ.id) ?? 0,
    title: series(SeriesQ.title) ?? '',
    description: series(SeriesQ.description) ?? EMPTY_RICH_TEXT_VALUE,
    createdAt: DateTime.fromISO(`${series(SeriesQ.created_at)}`),
    hosts: hosts.map(host => ({
      ...userDetailsFromQuery(
        [
          'avatarUrl',
          'bio',
          'displayName',
          'id',
          'points',
          'pronouns',
          'roles',
          'username',
          'activeAnniversary',
        ],
        { user: host }
      ),
      role: host(SeriesHostQ.role) ?? 'alternative',
    })),
  }
}

export function sessionDetailsFromQuery({
  session,
  userId,
}: {
  session: Query
  userId: number
}): SessionDetails {
  const worldId = session(SessionQ.world_id)
  const seriesId = session(SessionQ.series_id)

  const hosts = session.many(
    SessionHostQ.where({
      sessionId: SessionQ.id,
      approved: true,
      include: [{ association: 'User', required: true }],
      order: [[SessionHostQ.role, 'desc']],
    })
  )

  const registration = session.one(
    SessionRegistrationQ.where({
      sessionId: SessionQ.id,
      userId,
    })
  )
  const isUserDropIn = registration(SessionRegistrationQ.type) === 'series-dropin'
  const seriesBase = seriesBaseFromQuery({ series: session })
  const worldBase = worldBaseFromQuery({ world: session })

  const learnerFeedbacks = session.many(
    LearnerFeedbackQ.where({
      sessionId: SessionQ.id,
      revieweeUserId: userId,
      type: LEARNER_FEEDBACK_TYPE_TUTOR_TO_LEARNER,
      include: [
        {
          association: 'Reviewer',
          required: true,
        },
      ],
    })
  )
  const receivedLearnerFeedback = learnerFeedbacks.map(feedback => ({
    id: feedback(LearnerFeedbackQ.id) ?? 0,
    comment: feedback(LearnerFeedbackQ.comment) ?? '',
    reviewerDisplayName: feedback(UserQ.display_name) ?? '',
  }))

  return {
    id: session(SessionQ.id) ?? 0,
    createdAt: DateTime.fromISO(`${session(SessionQ.created_at)}`),
    userId: session(SessionQ.user_id) ?? 0,
    startDate: DateTime.fromISO(`${session(SessionQ.start_date)}`),
    title: session(SessionQ.getDisplayTitle()) ?? '',
    description: session(SessionQ.description) ?? EMPTY_RICH_TEXT_VALUE,
    hosts: hosts.map(host => ({
      ...userDetailsFromQuery(
        [
          'avatarUrl',
          'bio',
          'displayName',
          'id',
          'points',
          'pronouns',
          'roles',
          'username',
          'activeAnniversary',
        ],
        { user: host }
      ),
      role: host(SessionHostQ.role) ?? 'alternative',
    })),
    topic: topicDetailsFromQuery({ topic: session }),
    endDate: DateTime.fromISO(`${session(SessionQ.end_date)}`),
    countRegistrations: session(SessionQ.countSessionRegistration()) ?? 0,
    maxLearners: session(SessionQ.max_learners) ?? 0,
    isUserRegistered: session(SessionQ.hasUserRegistered(userId)) ?? false,
    isUserSelfReserved: session(SessionQ.hasUserSelfReservedSeries(userId)) ?? false,
    world: worldId == null ? null : worldBase,
    series: seriesId == null ? null : seriesBase,
    receivedLearnerFeedback,
    attendancePolicy: session(SessionQ.attendance_policy) ?? 'optional',
    isUserDropIn,
  }
}

export function seriesDetailsFromQuery({
  series,
  userId,
}: {
  series: Query
  userId: number
}): SeriesDetails {
  const nextSession = series.one(SeriesQ.getNextSession())

  const sessionStartDates = series
    .many(SeriesQ.getSessions())
    .map(session => DateTime.fromISO(`${session(SessionQ.start_date)}`))
  const seriesTimeInfo = getSeriesTimeInfo({ sessionStartDates })
  const numSessions = sessionStartDates.length

  const world = series.one(SeriesQ.getWorld())
  const course = series.one(SeriesQ.getCourse())
  const user = series.one(SeriesQ.getUser())

  const courseName = course(CourseQ.name) ?? ''
  const templateId = series(SeriesQ.series_template_id)

  return {
    id: series(SeriesQ.id) ?? 0,
    createdAt: DateTime.fromISO(`${series(SeriesQ.created_at)}`),
    userId: series(SeriesQ.user_id) ?? 0,
    title: series(SeriesQ.title) ?? '',
    description: series(SeriesQ.description) ?? EMPTY_RICH_TEXT_VALUE,
    countRegistrations: series(SeriesQ.countRegistrations()) ?? 0,
    maxLearners: series(SeriesQ.max_learners) ?? 0,
    hasEnded: series(SeriesQ.hasEnded()) ?? false,
    isEnrollmentClosed: series(SeriesQ.isEnrollmentClosed()) ?? false,
    isUserRegistered: series(SeriesQ.isUserRegistered(userId)) ?? false,
    isUserSelfReserved: series(SeriesQ.isUserSelfReserved(userId)) ?? false,
    startDate: DateTime.fromISO(`${series(SeriesQ.getComputedStartDate())}`),
    endedAt: DateTime.fromISO(`${series(SeriesQ.closed_at)}`),
    world: worldBaseFromQuery({ world }),
    isRecurringTemplateSeries: series(SeriesQ.isRecurringTemplateSeries()) ?? false,
    seriesTimeInfo,

    nextSession: {
      startDate: DateTime.fromISO(`${nextSession(SessionQ.start_date)}`),
      endDate: DateTime.fromISO(`${nextSession(SessionQ.end_date)}`),
      index: series(SeriesQ.countCompletedSessions()) ?? 0,
    },

    hosts: [
      {
        ...userDetailsFromQuery(
          ['avatarUrl', 'displayName', 'id', 'username', 'activeAnniversary', 'points'],
          { user }
        ),
        role: 'main',
      },
    ],
    courseName,
    numSessions,
    seriesTemplateId: templateId,
  }
}

export function channelDetailsFromQuery<K extends (keyof ChannelDetails)[]>(
  fields: [...K],
  { channel, userId }: { channel: Query; userId: number }
): Pick<ChannelDetails, K[number]> {
  return getFieldsFromQuery({
    fields,
    params: { query: channel, userId },
    getter: CHANNEL_QUERY_TO_FIELDS,
  })
}

export function featuredFeedbackDetailsFromSessionFeedbackQuery(
  sessionFeedback: Query
): FeaturedFeedback {
  return {
    id: sessionFeedback(SessionFeedbackQ.id) ?? 0,
    type: 'session',
    comment: sessionFeedback(SessionFeedbackQ.comment) ?? '',
    date: DateTime.fromISO(`${sessionFeedback(SessionFeedbackQ.created_at)}`),
  }
}

export function featuredFeedbackDetailsFromTutorAuditQuery(
  tutorAuditFeatured: Query
): FeaturedFeedback {
  const isNewAudit = tutorAuditFeatured(TutorAuditQ.isUsingAuditorRubricVersion()) ?? false

  return {
    id: tutorAuditFeatured(TutorAuditQ.id) ?? 0,
    type: isNewAudit ? 'audit' : 'old-audit',
    comment: tutorAuditFeatured(TutorAuditFeaturedQ.getComment()) ?? '',
    date: DateTime.fromISO(`${tutorAuditFeatured(TutorAuditQ.created_at)}`),
    auditRubricQuestionSlug: tutorAuditFeatured(TutorAuditFeaturedQ.comment_key),
  }
}

export function featuredFeedbackDetailsFromPostFeedbackQuery(
  postFeedback: Query
): FeaturedFeedback {
  return {
    id: postFeedback(PostFeedbackQ.id) ?? 0,
    type: 'post',
    comment: postFeedback(PostFeedbackQ.note) ?? '',
    date: DateTime.fromISO(`${postFeedback(PostFeedbackQ.created_at)}`),
  }
}

export function sessionTemplateDetailsFromQuery({
  sessionTemplate,
  excludeSeriesTemplate,
}: {
  sessionTemplate: Query
  excludeSeriesTemplate?: boolean
}): SessionTemplateDetails {
  const seriesTemplate =
    excludeSeriesTemplate === true
      ? null
      : sessionTemplate.one(
          SeriesTemplateQ.where(
            {
              id: SessionTemplateQ.series_template_id,
              include: [
                {
                  association: 'World',
                  required: false,
                },
                {
                  association: 'User',
                  required: true,
                },
                {
                  association: 'Course',
                  required: false,
                },
                {
                  association: 'Training',
                  required: false,
                },
              ],
            },
            SQL`${SessionTemplateQ.series_template_id} IS NOT NULL`
          )
        )

  const seriesTemplateId = seriesTemplate != null ? seriesTemplate(SeriesTemplateQ.id) : null
  const seriesTemplateDetails =
    seriesTemplate != null
      ? // eslint-disable-next-line @typescript-eslint/no-use-before-define
        seriesTemplateDetailsFromQuery(seriesTemplate)
      : null

  // world_id is nullable on session templates
  // Since `worldBaseFromQuery` coalesces null values, we'll see maybeWorld.id == 0
  const maybeWorld = worldBaseFromQuery({ world: sessionTemplate })

  const hasTopic = sessionTemplate(TopicQ.id) != null
  const topicDetails = {
    id: sessionTemplate(TopicQ.id) ?? 0,
    name: sessionTemplate(TopicQ.name) ?? '',
  }

  return {
    id: sessionTemplate(SessionTemplateQ.id) ?? 0,
    title: sessionTemplate(SessionTemplateQ.title) ?? '',
    description: sessionTemplate(SessionTemplateQ.description) ?? EMPTY_RICH_TEXT_VALUE,
    externalDescription: sessionTemplate(SessionTemplateQ.external_description),
    curriculum: sessionTemplate(SessionTemplateQ.curriculum),
    questionnaire: sessionTemplate(SessionTemplateQ.questionnaire),
    maxLearners: sessionTemplate(SessionTemplateQ.max_learners) ?? 0,
    startDate:
      sessionTemplate(SessionTemplateQ.start_date) == null
        ? null
        : DateTime.fromISO(`${sessionTemplate(SessionTemplateQ.start_date)}`),
    durationMinutes: sessionTemplate(SessionTemplateQ.duration_minutes) ?? 0,
    allowCohosts: sessionTemplate(SessionTemplateQ.allow_cohosts) ?? false,
    active: sessionTemplate(SessionTemplateQ.active) ?? false,
    seriesPosition: sessionTemplate(SessionTemplateQ.series_position),
    attendancePolicy: sessionTemplate(SessionTemplateQ.attendance_policy) ?? 'required',
    user: userDetailsFromQuery(
      [
        'avatarUrl',
        'bio',
        'displayName',
        'id',
        'points',
        'pronouns',
        'roles',
        'username',
        'activeAnniversary',
      ],
      { user: sessionTemplate }
    ),
    topic: hasTopic ? topicDetails : null,
    worldId: maybeWorld.id === 0 ? null : maybeWorld.id,
    world: maybeWorld.id === 0 ? null : maybeWorld,
    createdAt: DateTime.fromISO(`${sessionTemplate(SessionTemplateQ.created_at)}`),
    isBreakoutEnabled: sessionTemplate(SessionTemplateQ.is_breakout_enabled) ?? false,
    seriesTemplate: seriesTemplateId != null ? seriesTemplateDetails : null,
    // TODO: implement required trainings for session templates
    tutorTraining: null,
  }
}

export function seriesTemplateDetailsFromQuery(seriesTemplate: Query): SeriesTemplateDetails {
  const seriesTemplateAssessments = seriesTemplate.many(
    SeriesTemplateAssessmentQ.where({
      seriesTemplateId: SeriesTemplateQ.id,
    })
  )
  const sessionTemplateDetails = seriesTemplate
    .many(
      SessionTemplateQ.where({
        seriesTemplateId: SeriesTemplateQ.id,
        include: [
          {
            association: 'User',
            required: true,
          },
          {
            association: 'Topic',
            required: false,
          },
        ],
        order: [[SessionTemplateQ.series_position, 'asc']],
      })
    )
    .map(sessionTemplate =>
      sessionTemplateDetailsFromQuery({ sessionTemplate, excludeSeriesTemplate: true })
    )

  // world_id/course_id nullable on series templates
  // Since we must coalesce null values, we'll see maybeWorld.id/maybeCourse.id == 0 if they're null
  const maybeWorld = worldBaseFromQuery({ world: seriesTemplate })

  const maybeCourse = {
    id: seriesTemplate(CourseQ.id) ?? 0,
    name: seriesTemplate(CourseQ.name) ?? '',
    slug: seriesTemplate(CourseQ.slug) ?? '',
  }

  const tutorTrainingId = seriesTemplate(SeriesTemplateQ.tutor_training_id)
  const maybeTutorTraining = {
    id: seriesTemplate(SeriesTemplateQ.tutor_training_id) ?? 0,
    title: seriesTemplate(TrainingQ.title) ?? '',
    slug: seriesTemplate(TrainingQ.slug) ?? '',
    linkTraining: seriesTemplate(TrainingQ.link_training) ?? '',
  }

  return {
    id: seriesTemplate(SeriesTemplateQ.id) ?? 0,
    slug: seriesTemplate(SeriesTemplateQ.slug),
    title: seriesTemplate(SeriesTemplateQ.title) ?? '',
    description: seriesTemplate(SeriesTemplateQ.description) ?? EMPTY_RICH_TEXT_VALUE,
    externalDescription:
      seriesTemplate(SeriesTemplateQ.external_description) ?? EMPTY_RICH_TEXT_VALUE,
    curriculum: seriesTemplate(SeriesTemplateQ.curriculum),
    curriculumUrl: seriesTemplate(SeriesTemplateQ.curriculum_url),
    attendancePolicy: seriesTemplate(SeriesTemplateQ.attendance_policy) ?? '',
    questionnaire: seriesTemplate(SeriesTemplateQ.questionnaire),
    scheduleType: seriesTemplate(SeriesTemplateQ.schedule_type) ?? 'flexible',
    maxLearners: seriesTemplate(SeriesTemplateQ.max_learners) ?? 0,
    allowCohosts: seriesTemplate(SeriesTemplateQ.allow_cohosts) ?? false,
    active: seriesTemplate(SeriesTemplateQ.active) ?? false,
    enabled: seriesTemplate(SeriesTemplateQ.enabled) ?? false,
    countSessions: seriesTemplate(SeriesTemplateQ.countSessions()) ?? 0,
    user: userDetailsFromQuery(
      [
        'avatarUrl',
        'bio',
        'displayName',
        'id',
        'points',
        'pronouns',
        'roles',
        'username',
        'activeAnniversary',
      ],
      { user: seriesTemplate }
    ),
    course: maybeCourse.id === 0 ? null : maybeCourse,
    worldId: maybeWorld.id === 0 ? null : maybeWorld.id,
    world: maybeWorld.id === 0 ? null : maybeWorld,
    assessmentIds: seriesTemplateAssessments.map(
      assessment => assessment(SeriesTemplateAssessmentQ.assessment_id) ?? 0
    ),
    gatingGroup: seriesTemplate(SeriesTemplateQ.gating_group),
    minimumStartDate:
      seriesTemplate(SeriesTemplateQ.minimum_start_date) != null
        ? DateTime.fromISO(`${seriesTemplate(SeriesTemplateQ.minimum_start_date)}`)
        : null,
    type: seriesTemplate(SeriesTemplateQ.type) ?? SERIES_TEMPLATE_TYPE_STANDARD,
    maxRegistrationsPerLearner: seriesTemplate(SeriesTemplateQ.max_registrations_per_learner),
    maxActiveSeriesPerTutor: seriesTemplate(SeriesTemplateQ.max_active_series_per_tutor),
    peerReviewTags: seriesTemplate(SeriesTemplateQ.peer_review_tags),
    peerReviewVisibility:
      seriesTemplate(SeriesTemplateQ.peer_review_visibility) ??
      SeriesTemplatePeerReviewVisibility.Public,
    tutorTraining: tutorTrainingId == null ? null : maybeTutorTraining,
    createdAt: DateTime.fromISO(`${seriesTemplate(SeriesTemplateQ.created_at)}`),
    sessionTemplateDetails,
  }
}

export function blogArticleDetailsFromQuery(blogArticle: Query): BlogArticleDetails {
  const authors = blogArticle.many(
    BlogArticleAuthorQ.where({
      blogArticleId: BlogArticleQ.id,
      include: [
        {
          association: 'User',
          required: true,
        },
      ],
    })
  )
  const user = blogArticle.one(BlogArticleQ.getUser())

  return {
    id: blogArticle(BlogArticleQ.id) ?? 0,
    authors: authors.map(author =>
      userDetailsFromQuery(['avatarUrl', 'displayName', 'id', 'points', 'username'], {
        user: author,
      })
    ),
    user: userDetailsFromQuery(['avatarUrl', 'displayName', 'id', 'points', 'username'], {
      user,
    }),
    body: blogArticle(BlogArticleQ.body) ?? EMPTY_RICH_TEXT_VALUE,
    title: blogArticle(BlogArticleQ.title) ?? '',
    slug: blogArticle(BlogArticleQ.slug) ?? '',
    excerpt: blogArticle(BlogArticleQ.excerpt) ?? '',
    metaDescription: blogArticle(BlogArticleQ.meta_description) ?? '',
    category: blogArticle(BlogArticleQ.category) ?? 'community',
    pinnedTo: blogArticle(BlogArticleQ.pinned_to),
    imageUrl: blogArticle(BlogArticleQ.image_url),
    publishedAt:
      blogArticle(BlogArticleQ.published_at) == null
        ? null
        : DateTime.fromISO(`${blogArticle(BlogArticleQ.published_at)}`),
    createdAt: DateTime.fromISO(`${blogArticle(BlogArticleQ.created_at)}`),
  }
}

export function userVolunteerRoleDetailsFromQuery(
  userVolunteerRole: Query
): UserVolunteerRoleDetails {
  const volunteerRole = userVolunteerRole.one(
    VolunteerRoleQ.where({
      id: UserVolunteerRoleQ.volunteer_role_id,
      include: [{ association: 'VolunteerTeam', required: true }],
    })
  )

  const worldId = userVolunteerRole(UserVolunteerRoleQ.world_id)
  const world = userVolunteerRole.one(
    WorldQ.where({
      id: UserVolunteerRoleQ.world_id,
    })
  )
  const maybeWorldDetails = worldDetailsFromQuery(['id', 'title', 'slug'], { world, userId: null })

  return {
    id: userVolunteerRole(UserVolunteerRoleQ.id) ?? 0,
    userId: userVolunteerRole(UserVolunteerRoleQ.user_id) ?? 0,
    roleId: userVolunteerRole(UserVolunteerRoleQ.volunteer_role_id) ?? 0,
    roleName: volunteerRole(VolunteerRoleQ.name) ?? '',
    teamName: volunteerRole(VolunteerTeamQ.name) ?? '',
    teamSlug: volunteerRole(VolunteerTeamQ.slug) ?? '',
    iconUrl: volunteerRole(VolunteerTeamQ.icon_url) ?? '',
    startDate: DateTime.fromISO(`${userVolunteerRole(UserVolunteerRoleQ.start_date)}`),
    endDate:
      userVolunteerRole(UserVolunteerRoleQ.end_date) == null
        ? null
        : DateTime.fromISO(`${userVolunteerRole(UserVolunteerRoleQ.end_date)}`),
    world: worldId == null ? null : maybeWorldDetails,
  }
}
